/**
 * Görev Yönetimi — Faz 1
 *
 * Özellikler:
 *   • KPI kartları (açık, devam, gecikmiş, tamamlandı)
 *   • Filtreler: Kapsam (bana atanmış / oluşturduğum / hepsi) + durum + öncelik
 *   • Görev listesi (kart görünümü)
 *   • Yeni Görev diyaloğu (başlık, açıklama, öncelik, son tarih, ana + destek atama)
 *   • Görev Detay diyaloğu: alt görevler, yorumlar, geçmiş, status değiştirme
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ListTodo, Plus, Calendar, Clock, User as UserIcon, Users, AlertTriangle,
  CheckCircle2, PlayCircle, Circle, Trash2, MessageSquare, ChevronDown,
  ChevronUp, Loader2, X, History as HistoryIcon, Send, Sparkles, Link as LinkIcon,
} from "lucide-react";
import { toast } from "sonner";
import { matchesAll } from "@/lib/trFold";

// --- Constants -----
const PRIORITY_COLOR = {
  dusuk: "border-slate-800 text-slate-400 bg-slate-900/40",
  normal: "border-sky-900/60 text-sky-300 bg-sky-950/30",
  yuksek: "border-blue-900/60 text-blue-300 bg-blue-950/30",
  kritik: "border-red-900/60 text-red-300 bg-red-950/30",
};
const STATUS_META = {
  acik: { label: "Açık", icon: Circle, color: "text-slate-400" },
  devam: { label: "Devam", icon: PlayCircle, color: "text-blue-400" },
  beklemede: { label: "Beklemede", icon: Clock, color: "text-amber-400" },
  tamamlandi: { label: "Tamamlandı", icon: CheckCircle2, color: "text-emerald-400" },
  iptal: { label: "İptal", icon: X, color: "text-slate-600" },
};
const trLower = (s) => (s || "").toLocaleLowerCase("tr-TR");

const dtFmt = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "short" }) +
           " " + d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};
const isOverdue = (t) =>
  t.due_date && !["tamamlandi", "iptal"].includes(t.status) &&
  new Date(t.due_date) < new Date();

// --- Main Page -----
export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ statuses: [], priorities: [] });
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [scope, setScope] = useState("mine");
  const [status, setStatus] = useState("all");
  const [priority, setPriority] = useState("all");
  const [search, setSearch] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiData, setAiData] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const customerFilter = searchParams.get("customer_id");
  const orderFilter = searchParams.get("order_id");
  const deepLinkTaskId = searchParams.get("task_id");

  // FAZ 1 PUSH — Bildirime dokununca ilgili görev otomatik açılsın.
  useEffect(() => {
    if (deepLinkTaskId) setDetailId(deepLinkTaskId);
  }, [deepLinkTaskId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (scope !== "all") params.scope = scope;
      if (status !== "all") params.status = status;
      if (priority !== "all") params.priority = priority;
      if (customerFilter) params.customer_id = customerFilter;
      if (orderFilter) params.order_id = orderFilter;
      const { data } = await api.get("/tasks", { params });
      setTasks(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [scope, status, priority, customerFilter, orderFilter]);

  const loadAi = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.get("/tasks/ai/redistribution-suggestions");
      setAiData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAiLoading(false); }
  };

  useEffect(() => { load(); }, [load]);

  const reloadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/tasks/stats/summary");
      setStats(data);
    } catch {}
  }, []);
  useEffect(() => { reloadStats(); }, [reloadStats, tasks.length]);

  useEffect(() => {
    api.get("/tasks/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
  }, []);

  const filtered = useMemo(() => {
    const q = trLower(search.trim());
    if (!q) return tasks;
    return tasks.filter((t) => matchesAll(q, [t.title, t.description, t.main_assignee_email, ...(t.tags || [])]));
  }, [tasks, search]);

  return (
    <div className="pb-10">
      <PageHeader
        title="Görev Yönetimi"
        subtitle="Faz 1-4 · CRUD, atama, devretme, ekler, ERP entegrasyonu + AI"
        icon={ListTodo}
        actions={
          <div className="flex items-center gap-2">
            <a
              href="/gorevler/yonetici"
              data-testid="admin-panel-link"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-slate-800 text-xs uppercase tracking-widest font-mono text-slate-300 hover:bg-slate-900 hover:text-blue-400 transition-colors"
              title="Yönetici paneli"
            >
              <UserIcon className="w-3.5 h-3.5" /> Yönetici
            </a>
            <Button
              data-testid="ai-suggestions-btn"
              onClick={() => { setAiOpen(true); if (!aiData) loadAi(); }}
              variant="outline"
              className="border-purple-800 bg-transparent text-purple-300 hover:bg-purple-950/30 rounded-sm uppercase tracking-wider text-xs"
            >
              <Sparkles className="w-4 h-4 mr-1" /> AI Önerileri
            </Button>
            <Button
              data-testid="new-task-btn"
              onClick={() => setNewOpen(true)}
              className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold"
            >
              <Plus className="w-4 h-4 mr-1" /> Yeni Görev
            </Button>
          </div>
        }
      />

      <div className="p-6 lg:p-8 space-y-6">
        {(customerFilter || orderFilter) && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-sm bg-blue-950/30 border border-blue-900/60 text-xs" data-testid="task-erp-filter-banner">
            <LinkIcon className="w-3.5 h-3.5 text-blue-400" />
            <span className="text-blue-300 font-mono uppercase tracking-wider">ERP filtresi:</span>
            <span className="text-blue-100 font-semibold">
              {customerFilter ? `Müşteri: ${customerFilter.slice(0, 8)}…` : `Sipariş: ${orderFilter.slice(0, 8)}…`}
            </span>
            <a href="/gorevler" className="ml-auto text-blue-300 hover:text-blue-100 underline text-[11px]">Temizle</a>
          </div>
        )}
        {/* KPI cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <KpiCard label="Açık" value={stats.acik} color="bg-slate-700" icon={Circle}
              onClick={() => { setStatus("acik"); setScope("mine"); }}
              data-testid="task-kpi-open" />
            <KpiCard label="Devam" value={stats.devam} color="bg-blue-600" icon={PlayCircle}
              onClick={() => { setStatus("devam"); setScope("mine"); }}
              data-testid="task-kpi-progress" />
            <KpiCard label="Beklemede" value={stats.beklemede} color="bg-amber-600" icon={Clock}
              onClick={() => { setStatus("beklemede"); setScope("mine"); }}
              data-testid="task-kpi-pending" />
            <KpiCard label="Gecikmiş" value={stats.gecikmis} color="bg-red-600" icon={AlertTriangle}
              onClick={() => { setStatus("all"); setScope("mine"); }}
              data-testid="task-kpi-overdue" />
            <KpiCard label="Tamamlandı" value={stats.tamamlandi} color="bg-emerald-600" icon={CheckCircle2}
              onClick={() => { setStatus("tamamlandi"); setScope("mine"); }}
              data-testid="task-kpi-completed" />
          </div>
        )}

        {/* Filters */}
        <Card className="bg-slate-900 border-slate-800 rounded-sm p-3 flex flex-wrap items-center gap-2">
          <Input
            data-testid="task-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Görev ara: başlık, açıklama, atanan…"
            className="max-w-xs bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 h-9 text-xs"
          />
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger data-testid="task-scope" className="w-40 bg-slate-950 border-slate-700 rounded-sm h-9 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mine">Bana ilgili</SelectItem>
              <SelectItem value="assigned">Bana atanmış</SelectItem>
              <SelectItem value="created">Oluşturduğum</SelectItem>
              <SelectItem value="all">Tümü</SelectItem>
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="task-filter-status" className="w-36 bg-slate-950 border-slate-700 rounded-sm h-9 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm durumlar</SelectItem>
              {meta.statuses?.map((s) => (
                <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={priority} onValueChange={setPriority}>
            <SelectTrigger data-testid="task-filter-priority" className="w-36 bg-slate-950 border-slate-700 rounded-sm h-9 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm öncelikler</SelectItem>
              {meta.priorities?.map((p) => (
                <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="ml-auto text-[11px] font-mono text-slate-500">
            {loading ? "" : `${filtered.length} görev`}
          </div>
        </Card>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <Card className="bg-slate-900 border-slate-800 rounded-sm p-12 text-center">
            <ListTodo className="w-10 h-10 mx-auto text-slate-700 mb-3" />
            <div className="text-slate-400 text-sm">Bu filtrelere uygun görev yok.</div>
            <div className="text-slate-500 text-[11px] mt-1 font-mono">Yeni Görev butonuyla oluşturabilirsiniz.</div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map((t) => (
              <TaskCard key={t.id} task={t} onOpen={() => setDetailId(t.id)} />
            ))}
          </div>
        )}
      </div>

      <NewTaskDialog
        open={newOpen} onOpenChange={setNewOpen}
        users={users} meta={meta}
        onCreated={() => { setNewOpen(false); load(); }}
      />
      {detailId && (
        <TaskDetailDialog
          taskId={detailId}
          users={users} meta={meta}
          onClose={() => setDetailId(null)}
          onChanged={() => load()}
        />
      )}

      {/* AI Redistribution Suggestions Dialog */}
      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="rounded-sm sm:max-w-2xl bg-slate-900 border-slate-800 text-slate-100 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-400" /> AI Görev Yeniden Dağıtım Önerileri
            </DialogTitle>
          </DialogHeader>
          {aiLoading ? (
            <div className="py-8 flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
              <div className="text-xs text-slate-400 font-mono">AI analiz ediyor…</div>
            </div>
          ) : aiData ? (
            <div className="space-y-4">
              <section>
                <div className="text-[10px] uppercase font-mono text-slate-500 mb-2">İş Yükü Dağılımı</div>
                <div className="space-y-1 max-h-56 overflow-y-auto">
                  {(aiData.workload || []).length === 0 ? (
                    <div className="text-xs text-slate-500 italic">Aktif görev yok.</div>
                  ) : aiData.workload.map((w) => (
                    <div key={w.email} data-testid={`ai-workload-${w.email}`}
                      className="flex items-center gap-2 py-1.5 px-2 rounded-sm bg-slate-950/50 border border-slate-800 text-xs">
                      <span className="text-blue-300 font-mono truncate flex-1">{w.email}</span>
                      <span className="text-slate-300 font-mono">
                        <b className="text-slate-100">{w.active}</b> aktif
                      </span>
                      {w.overdue > 0 && (
                        <span className="text-red-400 font-mono">
                          <b>{w.overdue}</b> gecikmiş
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </section>
              <section>
                <div className="text-[10px] uppercase font-mono text-purple-400 mb-2 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> AI Önerileri
                </div>
                <div
                  data-testid="ai-suggestions-text"
                  className="text-sm text-slate-200 bg-purple-950/20 border border-purple-900/40 rounded-sm p-3 whitespace-pre-wrap leading-relaxed"
                >
                  {aiData.suggestions || "Öneri yok."}
                </div>
              </section>
            </div>
          ) : (
            <div className="py-6 text-center text-xs text-slate-500">Öneri yükleniyor…</div>
          )}
          <DialogFooter>
            <Button
              data-testid="ai-refresh"
              onClick={loadAi}
              disabled={aiLoading}
              variant="outline"
              className="border-purple-800 bg-transparent text-purple-300 hover:bg-purple-950/30 rounded-sm"
            >
              {aiLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
              Yeniden Analiz Et
            </Button>
            <Button onClick={() => setAiOpen(false)} className="bg-slate-700 hover:bg-slate-600 rounded-sm">
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Small components -----
function KpiCard({ label, value, color, icon: Icon, onClick, ...rest }) {
  return (
    <Card
      {...rest}
      onClick={onClick}
      className="bg-slate-900 border-slate-800 rounded-sm p-4 cursor-pointer hover:border-blue-600/70 hover:bg-slate-800/50 transition-all"
    >
      <div className="flex items-start justify-between mb-2">
        <div className={`w-8 h-8 ${color} flex items-center justify-center rounded-sm`}>
          <Icon className="w-4 h-4 text-white" strokeWidth={2.5} />
        </div>
      </div>
      <div className="text-2xl font-bold text-slate-50 font-mono">{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mt-1">
        {label}
      </div>
    </Card>
  );
}

function TaskCard({ task, onOpen }) {
  const st = STATUS_META[task.status] || STATUS_META.acik;
  const StIcon = st.icon;
  const overdue = isOverdue(task);
  const doneCount = (task.subtasks || []).filter((s) => s.is_done).length;
  const totalSubs = (task.subtasks || []).length;
  return (
    <Card
      data-testid={`task-card-${task.id}`}
      onClick={onOpen}
      className={`bg-slate-900 border-slate-800 rounded-sm p-4 cursor-pointer hover:border-blue-600/60 hover:bg-slate-800/40 transition-all ${
        overdue ? "border-l-4 border-l-red-600" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="text-sm font-semibold text-slate-100 flex-1">{task.title}</div>
        <span className={`text-[10px] px-1.5 py-0.5 border rounded-sm uppercase font-mono ${PRIORITY_COLOR[task.priority] || PRIORITY_COLOR.normal}`}>
          {task.priority}
        </span>
      </div>
      {task.description && (
        <div className="text-xs text-slate-400 line-clamp-2 mb-2">{task.description}</div>
      )}
      <div className="flex items-center gap-3 text-[11px] font-mono text-slate-500 flex-wrap">
        <span className={`flex items-center gap-1 ${st.color}`}>
          <StIcon className="w-3 h-3" /> {st.label}
        </span>
        {task.due_date && (
          <span className={`flex items-center gap-1 ${overdue ? "text-red-400 font-semibold" : ""}`}>
            <Calendar className="w-3 h-3" /> {dtFmt(task.due_date)}
          </span>
        )}
        {task.main_assignee_email && (
          <span className="flex items-center gap-1 text-slate-400">
            <UserIcon className="w-3 h-3" /> {task.main_assignee_email.split("@")[0]}
          </span>
        )}
        {(task.support_assignee_emails?.length ?? 0) > 0 && (
          <span className="flex items-center gap-1 text-slate-400">
            <Users className="w-3 h-3" /> +{task.support_assignee_emails.length}
          </span>
        )}
        {totalSubs > 0 && (
          <span className="flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> {doneCount}/{totalSubs}
          </span>
        )}
        {(task.comments?.length ?? 0) > 0 && (
          <span className="flex items-center gap-1">
            <MessageSquare className="w-3 h-3" /> {task.comments.length}
          </span>
        )}
      </div>
    </Card>
  );
}

// --- New task dialog -----
function NewTaskDialog({ open, onOpenChange, onCreated, users, meta }) {
  const emptyForm = {
    title: "", description: "", priority: "normal", due_date: "",
    main_assignee_email: "", support_assignee_emails: [],
    completion_rule: "manual",
    order_id: "", customer_id: "", supplier_id: "",
  };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  useEffect(() => { if (!open) setForm(emptyForm); }, [open]); // eslint-disable-line
  useEffect(() => {
    if (!open) return;
    api.get("/customers").then((r) => setCustomers(r.data || [])).catch(() => {});
    api.get("/suppliers").then((r) => setSuppliers(r.data || [])).catch(() => {});
    api.get("/orders").then((r) => setOrders(r.data || [])).catch(() => {});
  }, [open]);

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Başlık gerekli"); return; }
    setSaving(true);
    try {
      await api.post("/tasks", {
        ...form,
        due_date: form.due_date ? new Date(form.due_date).toISOString() : "",
      });
      toast.success("Görev oluşturuldu");
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-xl bg-slate-900 border-slate-800 text-slate-100">
        <DialogHeader>
          <DialogTitle>Yeni Görev</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase font-mono text-slate-400">Başlık *</Label>
            <Input
              autoFocus data-testid="new-task-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Örn: Sipariş SO-2026-0050 için kumaş sipariş ver"
              className="bg-slate-950 border-slate-700 rounded-sm mt-1 focus-visible:ring-blue-500"
            />
          </div>
          <div>
            <Label className="text-xs uppercase font-mono text-slate-400">Açıklama</Label>
            <Textarea
              data-testid="new-task-description"
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Detaylar, notlar…"
              className="bg-slate-950 border-slate-700 rounded-sm mt-1 focus-visible:ring-blue-500 resize-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase font-mono text-slate-400">Öncelik</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="new-task-priority" className="bg-slate-950 border-slate-700 rounded-sm mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {meta.priorities?.map((p) => (
                    <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase font-mono text-slate-400">Son Tarih</Label>
              <Input
                data-testid="new-task-due"
                type="datetime-local"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                className="bg-slate-950 border-slate-700 rounded-sm mt-1 focus-visible:ring-blue-500 font-mono text-xs"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase font-mono text-slate-400">Ana Sorumlu</Label>
            <Select value={form.main_assignee_email || "__none__"} onValueChange={(v) => setForm({ ...form, main_assignee_email: v === "__none__" ? "" : v })}>
              <SelectTrigger data-testid="new-task-assignee" className="bg-slate-950 border-slate-700 rounded-sm mt-1">
                <SelectValue placeholder="Kullanıcı seçin" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Atanmadı —</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.id || u.email} value={u.email}>
                    {u.name || u.email} <span className="text-slate-500 ml-1 text-[11px]">({u.email})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase font-mono text-slate-400">
              Destek Personeli
              {form.support_assignee_emails.length > 0 && (
                <span className="ml-2 text-blue-400">({form.support_assignee_emails.length} seçili)</span>
              )}
            </Label>
            <div className="flex flex-wrap gap-1.5 mt-1 p-2 border border-slate-700 rounded-sm bg-slate-950 min-h-[42px]">
              {users
                .filter((u) => u.email && u.email !== form.main_assignee_email)
                .map((u) => {
                  const active = form.support_assignee_emails.includes(u.email);
                  return (
                    <button
                      key={u.email}
                      type="button"
                      data-testid={`new-task-support-${u.email}`}
                      onClick={() => {
                        const cur = form.support_assignee_emails;
                        setForm({
                          ...form,
                          support_assignee_emails: active
                            ? cur.filter((x) => x !== u.email)
                            : [...cur, u.email],
                        });
                      }}
                      className={`text-[11px] px-2 py-1 border rounded-sm font-mono transition-all ${
                        active
                          ? "bg-blue-950/50 border-blue-700 text-blue-200"
                          : "border-slate-700 text-slate-400 hover:border-blue-800 hover:text-blue-300"
                      }`}
                    >
                      {u.name || u.email.split("@")[0]}
                    </button>
                  );
                })}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase font-mono text-slate-400">
              Tamamlama Kuralı
              <span className="ml-2 text-[10px] normal-case font-sans text-slate-600">
                · birden fazla atanan varsa
              </span>
            </Label>
            <Select
              value={form.completion_rule}
              onValueChange={(v) => setForm({ ...form, completion_rule: v })}
            >
              <SelectTrigger data-testid="new-task-completion-rule" className="bg-slate-950 border-slate-700 rounded-sm mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">Manuel — sadece yönetici / atanan kişi durumu değiştirir</SelectItem>
                <SelectItem value="any">İlk tamamlayan yeterli</SelectItem>
                <SelectItem value="all">Tüm atananlar tamamlamalı</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="pt-2 border-t border-slate-800 space-y-2">
            <div className="text-[10px] uppercase font-mono text-slate-500">ERP Bağlantısı (opsiyonel)</div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-[11px] font-mono text-slate-500">Sipariş</Label>
                <Select value={form.order_id || "__none__"} onValueChange={(v) => setForm({ ...form, order_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="new-task-order" className="bg-slate-950 border-slate-700 rounded-sm mt-1 h-9 text-xs">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__none__">—</SelectItem>
                    {orders.slice(0, 100).map((o) => (
                      <SelectItem key={o.id} value={o.id}>{o.order_no} · {o.customer_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] font-mono text-slate-500">Müşteri</Label>
                <Select value={form.customer_id || "__none__"} onValueChange={(v) => setForm({ ...form, customer_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="new-task-customer" className="bg-slate-950 border-slate-700 rounded-sm mt-1 h-9 text-xs">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__none__">—</SelectItem>
                    {customers.slice(0, 200).map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] font-mono text-slate-500">Tedarikçi</Label>
                <Select value={form.supplier_id || "__none__"} onValueChange={(v) => setForm({ ...form, supplier_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="new-task-supplier" className="bg-slate-950 border-slate-700 rounded-sm mt-1 h-9 text-xs">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__none__">—</SelectItem>
                    {suppliers.slice(0, 200).map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm">
            İptal
          </Button>
          <Button
            data-testid="new-task-save"
            onClick={submit}
            disabled={saving || !form.title.trim()}
            className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
            Oluştur
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Detail dialog -----
function TaskDetailDialog({ taskId, onClose, onChanged, users, meta }) {
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newSub, setNewSub] = useState("");
  const [newComment, setNewComment] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [delegateOpen, setDelegateOpen] = useState(false);
  const [delegateForm, setDelegateForm] = useState({ to_email: "", reason: "", keep_readonly: true });
  const [uploadingFile, setUploadingFile] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/tasks/${taskId}`);
      setTask(data);
    } catch (e) { toast.error(formatApiError(e)); onClose(); }
    finally { setLoading(false); }
  }, [taskId, onClose]);

  useEffect(() => { load(); }, [load]);

  const patch = async (fields) => {
    try {
      const { data } = await api.patch(`/tasks/${taskId}`, fields);
      setTask(data);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addSub = async () => {
    if (!newSub.trim()) return;
    try {
      const { data } = await api.post(`/tasks/${taskId}/subtasks`, { title: newSub.trim() });
      setTask(data);
      setNewSub("");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleSub = async (s) => {
    try {
      const { data } = await api.patch(`/tasks/${taskId}/subtasks/${s.id}`, { is_done: !s.is_done });
      setTask(data);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteSub = async (s) => {
    try {
      const { data } = await api.delete(`/tasks/${taskId}/subtasks/${s.id}`);
      setTask(data);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    try {
      const { data } = await api.post(`/tasks/${taskId}/comments`, { text: newComment.trim() });
      setTask(data);
      setNewComment("");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async () => {
    if (!window.confirm("Görevi silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/tasks/${taskId}`);
      toast.success("Görev silindi");
      onChanged?.();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // --- Delegation ---
  const submitDelegate = async () => {
    if (!delegateForm.to_email) { toast.error("Alıcı seçin"); return; }
    if (!delegateForm.reason.trim()) { toast.error("Devir nedeni zorunludur"); return; }
    try {
      const { data } = await api.post(`/tasks/${taskId}/delegate`, delegateForm);
      setTask(data);
      setDelegateOpen(false);
      setDelegateForm({ to_email: "", reason: "", keep_readonly: true });
      toast.success("Devir isteği gönderildi");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resolveDelegation = async (delegationId, accept) => {
    let note = "";
    if (!accept) {
      note = window.prompt("Red nedeni (opsiyonel):") || "";
    }
    try {
      const { data } = await api.post(`/tasks/${taskId}/delegations/${delegationId}/resolve`, { accept, note });
      setTask(data);
      toast.success(accept ? "Devir kabul edildi" : "Devir reddedildi");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cancelDelegation = async (delegationId) => {
    if (!window.confirm("Devir isteğini iptal etmek istiyor musunuz?")) return;
    try {
      const { data } = await api.post(`/tasks/${taskId}/delegations/${delegationId}/cancel`);
      setTask(data);
      toast.success("Devir iptal edildi");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // --- Attachments ---
  const uploadFile = async (file) => {
    if (!file) return;
    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/tasks/${taskId}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setTask(data);
      toast.success(`${file.name} yüklendi`);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploadingFile(false); }
  };

  const deleteAttachment = async (attId) => {
    if (!window.confirm("Dosyayı silmek istediğinize emin misiniz?")) return;
    try {
      const { data } = await api.delete(`/tasks/${taskId}/attachments/${attId}`);
      setTask(data);
      toast.success("Dosya silindi");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // --- Mark done (multi-user completion) ---
  const markDone = async () => {
    try {
      const { data } = await api.post(`/tasks/${taskId}/mark-done`);
      setTask(data);
      toast.success("Kendi payınızı tamamlandı olarak işaretlediniz");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const pendingDel = (task?.delegations || []).find((d) => d.status === "pending");

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-sm sm:max-w-3xl bg-slate-900 border-slate-800 text-slate-100 max-h-[90vh] overflow-y-auto">
        {loading || !task ? (
          <div className="py-12 flex justify-center">
            <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-start gap-3 pr-6">
                <span className="flex-1">{task.title}</span>
                <span className={`text-[10px] px-1.5 py-0.5 border rounded-sm uppercase font-mono shrink-0 ${PRIORITY_COLOR[task.priority] || PRIORITY_COLOR.normal}`}>
                  {task.priority}
                </span>
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              {/* Pending delegation banner */}
              {pendingDel && (
                <div className="bg-amber-950/30 border border-amber-800/60 rounded-sm p-3 space-y-2" data-testid="pending-delegation-banner">
                  <div className="text-xs font-semibold text-amber-200 flex items-center gap-2">
                    <UserIcon className="w-3.5 h-3.5" /> Bekleyen Devir İsteği
                  </div>
                  <div className="text-xs text-amber-100">
                    <b>{pendingDel.from_email}</b> → <b className="text-blue-300">{pendingDel.to_email}</b>
                  </div>
                  <div className="text-[11px] text-amber-200/80 italic">"{pendingDel.reason}"</div>
                  {pendingDel.keep_readonly && (
                    <div className="text-[10px] text-slate-400 font-mono">
                      Kabul edilirse eski sahip sadece okuma yetkisiyle kalır.
                    </div>
                  )}
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" data-testid="delegation-accept"
                      onClick={() => resolveDelegation(pendingDel.id, true)}
                      className="bg-emerald-600 hover:bg-emerald-500 rounded-sm h-7 text-xs uppercase font-semibold">
                      Kabul
                    </Button>
                    <Button size="sm" data-testid="delegation-reject"
                      onClick={() => resolveDelegation(pendingDel.id, false)}
                      variant="outline"
                      className="border-slate-700 bg-transparent text-slate-300 hover:bg-red-950/30 hover:text-red-300 rounded-sm h-7 text-xs uppercase font-semibold">
                      Reddet
                    </Button>
                    <Button size="sm" data-testid="delegation-cancel"
                      onClick={() => cancelDelegation(pendingDel.id)}
                      variant="ghost"
                      className="text-slate-500 hover:text-red-400 hover:bg-red-950/20 rounded-sm h-7 text-xs">
                      İsteği İptal Et
                    </Button>
                  </div>
                </div>
              )}
              {task.description && (
                <div className="text-sm text-slate-300 bg-slate-950/40 border border-slate-800 rounded-sm p-3">
                  {task.description}
                </div>
              )}

              {/* Row of meta actions */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-[10px] uppercase font-mono text-slate-500">Durum</Label>
                  <Select value={task.status} onValueChange={(v) => patch({ status: v })}>
                    <SelectTrigger data-testid="detail-status" className="bg-slate-950 border-slate-700 rounded-sm mt-1 h-9 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {meta.statuses?.map((s) => (
                        <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase font-mono text-slate-500">Ana Sorumlu</Label>
                  <Select value={task.main_assignee_email || "__none__"} onValueChange={(v) => patch({ main_assignee_email: v === "__none__" ? "" : v })}>
                    <SelectTrigger data-testid="detail-assignee" className="bg-slate-950 border-slate-700 rounded-sm mt-1 h-9 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">—</SelectItem>
                      {users.map((u) => (
                        <SelectItem key={u.email} value={u.email}>{u.name || u.email}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase font-mono text-slate-500">Son Tarih</Label>
                  <Input
                    data-testid="detail-due"
                    type="datetime-local"
                    value={task.due_date ? task.due_date.slice(0, 16) : ""}
                    onChange={(e) => patch({ due_date: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                    className="bg-slate-950 border-slate-700 rounded-sm mt-1 h-9 text-xs font-mono"
                  />
                </div>
              </div>

              {/* Subtasks */}
              <section>
                <div className="text-xs uppercase font-mono text-slate-400 mb-2 flex items-center justify-between">
                  <span>Alt Görevler ({task.subtasks.filter((s) => s.is_done).length}/{task.subtasks.length})</span>
                </div>
                <div className="space-y-1">
                  {task.subtasks.map((s) => (
                    <div key={s.id} data-testid={`subtask-row-${s.id}`}
                      className="flex items-center gap-2 py-1.5 px-2 rounded-sm hover:bg-slate-800/40 group">
                      <button
                        type="button"
                        onClick={() => toggleSub(s)}
                        data-testid={`subtask-toggle-${s.id}`}
                        className="shrink-0"
                      >
                        {s.is_done ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-500" strokeWidth={2.5} />
                        ) : (
                          <Circle className="w-4 h-4 text-slate-600 hover:text-slate-300" />
                        )}
                      </button>
                      <span className={`text-xs flex-1 ${s.is_done ? "line-through text-slate-500" : "text-slate-200"}`}>
                        {s.title}
                      </span>
                      <button
                        type="button"
                        onClick={() => deleteSub(s)}
                        className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 transition-opacity"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  <div className="flex items-center gap-2 mt-2">
                    <Input
                      data-testid="subtask-add-input"
                      value={newSub}
                      onChange={(e) => setNewSub(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && newSub.trim()) addSub(); }}
                      placeholder="+ Alt görev ekle (Enter)"
                      className="bg-slate-950 border-slate-700 rounded-sm h-8 text-xs focus-visible:ring-blue-500"
                    />
                    <Button size="sm" onClick={addSub} disabled={!newSub.trim()} data-testid="subtask-add-btn"
                      className="bg-blue-600 hover:bg-blue-500 rounded-sm h-8 px-2">
                      <Plus className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </section>

              {/* Comments */}
              <section>
                <div className="text-xs uppercase font-mono text-slate-400 mb-2">
                  Yorumlar ({task.comments.length})
                </div>
                <div className="space-y-2">
                  {task.comments.map((c) => (
                    <div key={c.id} className="text-xs bg-slate-950/50 border border-slate-800 rounded-sm p-2">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-blue-400 font-semibold">{c.user_email.split("@")[0]}</span>
                        <span className="text-slate-600 text-[10px] font-mono">{dtFmt(c.created_at)}</span>
                      </div>
                      <div className="text-slate-200 whitespace-pre-wrap">{c.text}</div>
                    </div>
                  ))}
                  <div className="flex items-center gap-2 mt-2">
                    <Input
                      data-testid="comment-input"
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && newComment.trim()) addComment(); }}
                      placeholder="Yorum yaz (Enter)…"
                      className="bg-slate-950 border-slate-700 rounded-sm h-8 text-xs focus-visible:ring-blue-500"
                    />
                    <Button size="sm" onClick={addComment} disabled={!newComment.trim()} data-testid="comment-send-btn"
                      className="bg-blue-600 hover:bg-blue-500 rounded-sm h-8 px-2">
                      <Send className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </section>

              {/* Attachments */}
              <section>
                <div className="text-xs uppercase font-mono text-slate-400 mb-2 flex items-center justify-between">
                  <span>Dosya Ekleri ({task.attachments?.length || 0})</span>
                  <label className="text-[11px] font-mono uppercase text-emerald-400 hover:text-emerald-300 cursor-pointer flex items-center gap-1">
                    <input
                      type="file"
                      data-testid="attach-input"
                      className="hidden"
                      onChange={(e) => { uploadFile(e.target.files?.[0]); e.target.value = ""; }}
                    />
                    {uploadingFile ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                    Dosya Ekle
                  </label>
                </div>
                <div className="space-y-1">
                  {(task.attachments || []).map((a) => (
                    <div key={a.id} data-testid={`attachment-${a.id}`}
                      className="flex items-center gap-2 px-2 py-1.5 bg-slate-950/50 border border-slate-800 rounded-sm text-xs group">
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/tasks/${task.id}/attachments/${a.id}/download`}
                        target="_blank" rel="noreferrer"
                        className="flex-1 text-blue-300 hover:text-blue-200 truncate"
                      >
                        📎 {a.filename}
                      </a>
                      <span className="text-slate-500 font-mono text-[10px]">{(a.size / 1024).toFixed(1)} KB</span>
                      <span className="text-slate-600 text-[10px] font-mono">{a.uploaded_by?.split("@")[0]}</span>
                      <button onClick={() => deleteAttachment(a.id)}
                        className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400"
                        data-testid={`attachment-delete-${a.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {(!task.attachments || task.attachments.length === 0) && (
                    <div className="text-[11px] text-slate-500 italic px-2">
                      PDF / Excel / Word / resim ekleyebilirsiniz.
                    </div>
                  )}
                </div>
              </section>

              {/* Delegations history */}
              {(task.delegations || []).length > 0 && (
                <section>
                  <div className="text-xs uppercase font-mono text-slate-400 mb-2">
                    Devir Geçmişi ({task.delegations.length})
                  </div>
                  <div className="space-y-1">
                    {task.delegations.slice().reverse().map((d) => (
                      <div key={d.id} className="text-[11px] font-mono flex items-center gap-2 px-2 py-1 bg-slate-950/30 border border-slate-800/50 rounded-sm">
                        <span className={
                          d.status === "accepted" ? "text-emerald-400" :
                          d.status === "rejected" ? "text-red-400" :
                          d.status === "cancelled" ? "text-slate-500" : "text-amber-400"
                        }>{d.status}</span>
                        <span className="text-slate-500">{d.from_email?.split("@")[0]}</span>
                        <span className="text-slate-600">→</span>
                        <span className="text-blue-300">{d.to_email?.split("@")[0]}</span>
                        <span className="text-slate-500 italic truncate flex-1">"{d.reason}"</span>
                        <span className="text-slate-600 text-[10px]">{dtFmt(d.created_at)}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* History */}
              <section>
                <button
                  type="button"
                  onClick={() => setShowHistory((s) => !s)}
                  data-testid="detail-history-toggle"
                  className="text-xs uppercase font-mono text-slate-400 hover:text-slate-200 flex items-center gap-1"
                >
                  <HistoryIcon className="w-3 h-3" />
                  Geçmiş ({task.history.length}) {showHistory ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
                {showHistory && (
                  <div className="mt-2 space-y-1 max-h-56 overflow-y-auto text-[11px] font-mono text-slate-400 pl-1">
                    {task.history.slice().reverse().map((h) => (
                      <div key={h.id} className="flex gap-2 py-0.5">
                        <span className="text-slate-600">{dtFmt(h.created_at)}</span>
                        <span className="text-blue-400">{h.actor_email.split("@")[0]}</span>
                        <span className="text-slate-300">{h.action}</span>
                        {h.details && (
                          <span className="text-slate-500 truncate">
                            {Object.entries(h.details).map(([k, v]) => `${k}=${String(v).slice(0, 40)}`).join(" ")}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>

            <DialogFooter className="flex items-center justify-between pt-3 border-t border-slate-800 flex-wrap gap-2">
              <Button
                data-testid="detail-delete-btn"
                variant="ghost"
                onClick={del}
                className="text-slate-500 hover:text-red-400 hover:bg-red-950/30 rounded-sm text-xs"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Görevi Sil
              </Button>
              <div className="flex items-center gap-2 flex-wrap">
                {task.completion_rule !== "manual" && task.status !== "tamamlandi" && (
                  <Button
                    data-testid="mark-done-btn"
                    onClick={markDone}
                    disabled={task.completed_by?.includes((task.main_assignee_email || ""))}
                    className="bg-emerald-600 hover:bg-emerald-500 rounded-sm uppercase tracking-wider text-xs"
                    title={`Tamamlama kuralı: ${task.completion_rule === "any" ? "İlk tamamlayan yeterli" : "Herkes tamamlamalı"}`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Kendi Payımı Bitirdim
                  </Button>
                )}
                <Button
                  data-testid="delegate-btn"
                  onClick={() => {
                    setDelegateForm({ to_email: "", reason: "", keep_readonly: true });
                    setDelegateOpen(true);
                  }}
                  disabled={!!pendingDel}
                  variant="outline"
                  className="border-blue-800 bg-transparent text-blue-300 hover:bg-blue-950/30 rounded-sm uppercase tracking-wider text-xs"
                  title={pendingDel ? "Zaten bekleyen devir isteği var" : ""}
                >
                  <UserIcon className="w-3.5 h-3.5 mr-1" /> Devret
                </Button>
                <Button
                  onClick={onClose}
                  className="bg-slate-700 hover:bg-slate-600 rounded-sm uppercase tracking-wider text-xs"
                >
                  Kapat
                </Button>
              </div>
            </DialogFooter>

            {/* Delegate dialog */}
            <Dialog open={delegateOpen} onOpenChange={setDelegateOpen}>
              <DialogContent className="rounded-sm sm:max-w-md bg-slate-900 border-slate-800 text-slate-100">
                <DialogHeader>
                  <DialogTitle>Görevi Devret</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs uppercase font-mono text-slate-400">Yeni Sorumlu</Label>
                    <Select
                      value={delegateForm.to_email || "__none__"}
                      onValueChange={(v) => setDelegateForm({ ...delegateForm, to_email: v === "__none__" ? "" : v })}
                    >
                      <SelectTrigger data-testid="delegate-to" className="bg-slate-950 border-slate-700 rounded-sm mt-1">
                        <SelectValue placeholder="Kullanıcı seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Seçin —</SelectItem>
                        {users
                          .filter((u) => u.email && u.email !== task.main_assignee_email)
                          .map((u) => (
                            <SelectItem key={u.email} value={u.email}>{u.name || u.email}</SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase font-mono text-slate-400">Devir Nedeni *</Label>
                    <Textarea
                      data-testid="delegate-reason"
                      rows={3}
                      value={delegateForm.reason}
                      onChange={(e) => setDelegateForm({ ...delegateForm, reason: e.target.value })}
                      placeholder="Örn: 10 gün izne çıkıyorum. Devrin nedeni ve önemli notlar…"
                      className="bg-slate-950 border-slate-700 rounded-sm mt-1 focus-visible:ring-blue-500 resize-none"
                    />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-300">
                    <input
                      type="checkbox"
                      data-testid="delegate-readonly"
                      checked={delegateForm.keep_readonly}
                      onChange={(e) => setDelegateForm({ ...delegateForm, keep_readonly: e.target.checked })}
                      className="accent-blue-500"
                    />
                    <span>Kabul sonrası ben sadece <b>görüntüleme</b> yetkisiyle kalayım</span>
                  </label>
                  <div className="text-[11px] font-mono text-slate-500 bg-slate-950/40 border border-slate-800 rounded-sm p-2">
                    Devir gönderildiğinde alıcı <b>Kabul</b> veya <b>Reddet</b> demeden görev size ait kalır.
                    Tüm geçmiş (yorumlar, alt görevler, dosyalar, tarihler) korunur.
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDelegateOpen(false)}
                    className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm">
                    İptal
                  </Button>
                  <Button
                    data-testid="delegate-submit"
                    onClick={submitDelegate}
                    disabled={!delegateForm.to_email || !delegateForm.reason.trim()}
                    className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold"
                  >
                    <UserIcon className="w-4 h-4 mr-1" /> Devir Talebi Gönder
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
